import { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Badge, Table, Tag, Typography } from 'tdesign-react';
import { Link } from 'react-router-dom'; // 替换a标签，符合React路由规范
import {
  UserIcon,
  ShopIcon,
  CartIcon,
  FileIcon,
  ChartIcon,
  VerifyIcon,
} from 'tdesign-icons-react';
import { userApi, articleApi, supplierApi, buyerApi, productApi, marketingFormApi } from '../api';

// 解构Typography组件，统一文字样式
const { Title, Paragraph } = Typography;

function getAdminInfo(): any {
  try {
    const info = localStorage.getItem('adminInfo');
    return info ? JSON.parse(info) : null;
  } catch {
    return null;
  }
}

export default function Dashboard() {
  const adminInfo = getAdminInfo();
  const isAdmin = adminInfo?.userType === 1;

  const [stats, setStats] = useState({
    userCount: 0,
    articleCount: 0,
    supplierCount: 0,
    buyerCount: 0,
    productCount: 0,
    pendingProductCount: 0,
    pendingFormCount: 0,
  });
  const [recentForms, setRecentForms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAdmin) {
      loadStats();
      loadRecentForms();
    }
  }, []);

  const loadStats = async () => {
    try {
      const [userRes, articleRes, supplierRes, buyerRes, productRes, formRes] = await Promise.all([
        userApi.getList({ pageNum: 1, pageSize: 1 }),
        articleApi.getArticleList({ pageNum: 1, pageSize: 1 }),
        supplierApi.getList({ pageNum: 1, pageSize: 1 }),
        buyerApi.getList({ pageNum: 1, pageSize: 1 }),
        productApi.getList({ pageNum: 1, pageSize: 1 }),
        marketingFormApi.countPending(),
      ]);

      setStats({
        userCount: userRes.data?.total || 0,
        articleCount: articleRes.data?.total || 0,
        supplierCount: supplierRes.data?.total || 0,
        buyerCount: buyerRes.data?.total || 0,
        productCount: productRes.data?.total || 0,
        pendingProductCount: 0,
        pendingFormCount: formRes.data || 0,
      });
      setLoading(false); // 加载完成
    } catch (err) {
      console.error('加载统计数据失败:', err);
      setLoading(false);
    }
  };

  const loadRecentForms = async () => {
    try {
      const res = await marketingFormApi.getList({ pageNum: 1, pageSize: 5 });
      if (res.code === 200) {
        setRecentForms(res.data.list);
      }
    } catch (err) {
      console.error('加载最近表单失败:', err);
    }
  };

  const formColumns = [
    { colKey: 'contactName', title: '联系人', width: 120 },
    { colKey: 'contactPhone', title: '联系电话', width: 140 },
    { colKey: 'companyName', title: '公司名称', ellipsis: true },
    { colKey: 'formType', title: '表单类型', width: 120 },
    {
      colKey: 'status',
      title: '状态',
      width: 100,
      cell: ({ row }: any) => {
        const statusMap: Record<number, { label: string; theme: string }> = {
          0: { label: '待处理', theme: 'warning' },
          1: { label: '已联系', theme: 'primary' },
          2: { label: '已成交', theme: 'success' },
          3: { label: '已关闭', theme: 'default' },
        };
        const status = statusMap[row.status] || { label: '未知', theme: 'default' };
        return <Tag theme={status.theme as any}>{status.label}</Tag>;
      },
    },
    { colKey: 'createTime', title: '提交时间', width: 180 },
  ];

  // 统一样式常量（便于维护）
  const styles = {
    container: {
      padding: '0 8px', // 外层容器间距，适配侧边栏
    },
    statCard: {
      height: '100%', // 统计卡片等高
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)', // 轻微阴影，提升质感
    },
    quickEntryCard: {
      height: '100%',
      cursor: 'pointer',
      transition: 'all 0.2s ease', //  hover动画
    },
    quickEntryLink: {
      color: '#165DFF', // TDesign标准主色
      fontWeight: 500,
      textDecoration: 'none',
      '&:hover': {
        color: '#0E42C5', // hover深色
      },
    },
    tableContainer: {
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    },
  };

  if (!isAdmin) {
    return (
      <div style={styles.container}>
        <Title level={2} style={{ marginBottom: '16px', color: '#1D2129' }}>
          欢迎回来
        </Title>
        <Paragraph style={{ color: '#86909C', fontSize: '16px' }}>
          您好，{adminInfo?.nickname || adminInfo?.username || '用户'}！
        </Paragraph>
        <Paragraph style={{ color: '#86909C', fontSize: '14px', marginTop: '8px' }}>
          您可以通过左侧菜单访问已授权的功能模块。
        </Paragraph>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* 页面标题：用TDesign的Title组件，统一层级 */}
      <Title level={2} style={{ marginBottom: '24px', color: '#1D2129' }}>控制台</Title>

      {/* 统计卡片：调整Col比例 + 统一间距 + 等高卡片 */}
      <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
        {/* 调整span为6（24列布局，6=1/4宽度，更合理），xs/sm适配响应式 */}
        <Col span={6} xs={12} sm={8} md={6} lg={6}>
          <Card bordered style={styles.statCard}>
            <Statistic
              title="用户总数"
              value={stats.userCount}
              prefix={<UserIcon style={{ color: '#165DFF' }} />} // TDesign标准主色
              titleStyle={{ color: '#4E5969', fontSize: '14px' }} // 标题样式统一
              valueStyle={{ color: '#1D2129', fontSize: '24px', fontWeight: 600 }}
            />
          </Card>
        </Col>
        <Col span={6} xs={12} sm={8} md={6} lg={6}>
          <Card bordered style={styles.statCard}>
            <Statistic
              title="文章总数"
              value={stats.articleCount}
              prefix={<FileIcon style={{ color: '#00B42A' }} />} // TDesign标准成功色
              titleStyle={{ color: '#4E5969', fontSize: '14px' }}
              valueStyle={{ color: '#1D2129', fontSize: '24px', fontWeight: 600 }}
            />
          </Card>
        </Col>
        <Col span={6} xs={12} sm={8} md={6} lg={6}>
          <Card bordered style={styles.statCard}>
            <Statistic
              title="供应商"
              value={stats.supplierCount}
              prefix={<ShopIcon style={{ color: '#FF7D00' }} />} // TDesign标准警告色
              titleStyle={{ color: '#4E5969', fontSize: '14px' }}
              valueStyle={{ color: '#1D2129', fontSize: '24px', fontWeight: 600 }}
            />
          </Card>
        </Col>
        <Col span={6} xs={12} sm={8} md={6} lg={6}>
          <Card bordered style={styles.statCard}>
            <Statistic
              title="采购商"
              value={stats.buyerCount}
              prefix={<CartIcon style={{ color: '#722ED1' }} />} // TDesign标准紫色
              titleStyle={{ color: '#4E5969', fontSize: '14px' }}
              valueStyle={{ color: '#1D2129', fontSize: '24px', fontWeight: 600 }}
            />
          </Card>
        </Col>
        <Col span={6} xs={12} sm={8} md={6} lg={6}>
          <Card bordered style={styles.statCard}>
            <Statistic
              title="商品总数"
              value={stats.productCount}
              prefix={<VerifyIcon style={{ color: '#168CFF' }} />} // TDesign标准浅蓝色
              titleStyle={{ color: '#4E5969', fontSize: '14px' }}
              valueStyle={{ color: '#1D2129', fontSize: '24px', fontWeight: 600 }}
            />
          </Card>
        </Col>
        <Col span={6} xs={12} sm={8} md={6} lg={6}>
          <Card bordered style={styles.statCard}>
            <Statistic
              title="待处理表单"
              value={stats.pendingFormCount}
              prefix={<ChartIcon style={{ color: '#F53F3F' }} />} // TDesign标准危险色
              suffix={stats.pendingFormCount > 0 ? (
                <Badge count={stats.pendingFormCount} color="#F53F3F" />
              ) : null}
              titleStyle={{ color: '#4E5969', fontSize: '14px' }}
              valueStyle={{ color: '#1D2129', fontSize: '24px', fontWeight: 600 }}
            />
          </Card>
        </Col>
      </Row>

      {/* 最近提交的表单：优化卡片样式 + 表格内边距 */}
      <Card 
        title="最近提交的表单" 
        bordered={false} // 去掉边框，用阴影提升质感
        style={{ marginBottom: '24px', ...styles.tableContainer }}
        headerStyle={{ padding: '16px 24px', borderBottom: '1px solid #F2F3F5' }} // 表头间距
        bodyStyle={{ padding: '24px' }} // 表体内边距
      >
        <Table
          data={recentForms.length > 0 ? recentForms : []}
          columns={formColumns}
          rowKey="id"
          pagination={false}
          size="medium" // 调整表格尺寸，更舒适
          loading={loading} // 添加加载状态
          empty="暂无表单数据" // 空数据提示（更友好）
          tableStyle={{ border: '1px solid #F2F3F5' }}
        />
      </Card>

      {/* 快捷入口：优化Col比例 + 卡片hover效果 + 路由链接 */}
      <Row gutter={[24, 24]}>
        <Col span={6} xs={12} sm={8} md={6} lg={6}>
          <Card
            title="用户管理"
            actions={[
              <Link to="/admin/system/user" style={styles.quickEntryLink}>
                进入 &gt;
              </Link>
            ]}
            bordered={false}
            hoverShadow
            style={styles.quickEntryCard}
            bodyStyle={{ padding: '16px 24px' }}
          >
            <Paragraph style={{ color: '#86909C', margin: 0, fontSize: '14px' }}>
              管理系统用户信息
            </Paragraph>
          </Card>
        </Col>
        <Col span={6} xs={12} sm={8} md={6} lg={6}>
          <Card
            title="文章管理"
            actions={[
              <Link to="/admin/business/article" style={styles.quickEntryLink}>
                进入 &gt;
              </Link>
            ]}
            bordered={false}
            hoverShadow
            style={styles.quickEntryCard}
            bodyStyle={{ padding: '16px 24px' }}
          >
            <Paragraph style={{ color: '#86909C', margin: 0, fontSize: '14px' }}>
              管理资讯文章内容
            </Paragraph>
          </Card>
        </Col>
        <Col span={6} xs={12} sm={8} md={6} lg={6}>
          <Card
            title="商品审核"
            actions={[
              <Link to="/admin/business/product" style={styles.quickEntryLink}>
                进入 &gt;
              </Link>
            ]}
            bordered={false}
            hoverShadow
            style={styles.quickEntryCard}
            bodyStyle={{ padding: '16px 24px' }}
          >
            <Paragraph style={{ color: '#86909C', margin: 0, fontSize: '14px' }}>
              审核供应商提交的商品
            </Paragraph>
          </Card>
        </Col>
        <Col span={6} xs={12} sm={8} md={6} lg={6}>
          <Card
            title="营销表单"
            actions={[
              <Link to="/admin/business/marketing" style={styles.quickEntryLink}>
                进入 &gt;
              </Link>
            ]}
            bordered={false}
            hoverShadow
            style={styles.quickEntryCard}
            bodyStyle={{ padding: '16px 24px' }}
          >
            <Paragraph style={{ color: '#86909C', margin: 0, fontSize: '14px' }}>
              处理用户提交的联系方式
            </Paragraph>
          </Card>
        </Col>
      </Row>
    </div>
  );
}